import React from "react";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Pagination,
    PaginationItem,
    PaginationLink,
    Row,
    Table,
} from "reactstrap";
import FormInput from "../common/form/FormInput";
import DashboardService from "../../services/dashboardService";
import SecurityService from "../../services/securityService";

class ReportsKpiTable extends React.Component {
    constructor(props) {
        super(props);
        
        // Calculate current week (Monday to Sunday)
        const today = new Date();
        const dayOfWeek = today.getDay();
        const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const monday = new Date(today);
        monday.setDate(today.getDate() + diffToMonday);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        
        // Format dates as YYYY-MM-DD
        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        this.state = {
            filterType: "USER",
            dateFrom: formatDate(monday),
            dateTo: formatDate(sunday),
            data: [],
            loading: false,
            currentPage: 0,
            pageSize: 20,
            totalElements: 0,
            totalPages: 0,
            sortOrder: 'desc', // Default descending sort by total reports created
        };
    }

    componentDidMount() {
        // For ROLE_LEVEL3, force filterType to USER
        if (SecurityService.hasRole("ROLE_LEVEL3") || SecurityService.hasRole("ROLE_LEVEL_3")) {
            this.setState({ filterType: "USER" }, () => {
                this.fetchData();
            });
        } else {
            this.fetchData();
        }
    }

    onChange = (event) => {
        const { name, value } = event.target;
        
        // Prevent ROLE_LEVEL3 from changing filterType - force it to USER
        if (name === 'filterType' && (SecurityService.hasRole("ROLE_LEVEL3") || SecurityService.hasRole("ROLE_LEVEL_3"))) {
            return; // Ignore filterType changes for ROLE_LEVEL3
        }
        
        this.setState({ [name]: value }, () => {
            // Reset to page 0 when filters change
            if (name !== 'pageSize' && name !== 'currentPage') {
                this.setState({ currentPage: 0 }, () => {
                    this.fetchData();
                });
            } else if (name === 'pageSize') {
                this.setState({ currentPage: 0, pageSize: parseInt(value) }, () => {
                    this.fetchData();
                });
            }
        });
    };

    fetchData = () => {
        this.setState({ loading: true });
        let { filterType, dateFrom, dateTo, currentPage, pageSize } = this.state;
        
        // Force filterType to USER for ROLE_LEVEL3
        if (SecurityService.hasRole("ROLE_LEVEL3") || SecurityService.hasRole("ROLE_LEVEL_3")) {
            filterType = "USER";
        }
        
        DashboardService.getReportsKpi(dateFrom, dateTo, filterType, currentPage, pageSize)
            .then((response) => {
                // The axios interceptor already extracts response.data, so 'response' here is the DTO directly
                if (!response) {
                    console.error("No response found");
                    this.setState({
                        data: [],
                        totalElements: 0,
                        totalPages: 1,
                        loading: false,
                    });
                    return;
                }
                
                // response is already the DTO object, so access data directly
                const dataArray = Array.isArray(response.data) ? response.data : [];
                
                this.setState({
                    data: dataArray,
                    totalElements: response.totalElements || 0,
                    totalPages: response.totalPages || 1,
                    loading: false,
                });
            })
            .catch((error) => {
                console.error("Error fetching reports KPI:", error);
                this.setState({
                    data: [],
                    totalElements: 0,
                    totalPages: 1,
                    loading: false,
                });
            });
    };

    handlePageChange = (pageNumber) => {
        this.setState({ currentPage: pageNumber }, () => {
            this.fetchData();
        });
    };

    handleSort = () => {
        // Toggle between 'asc' and 'desc'
        const newSortOrder = this.state.sortOrder === 'desc' ? 'asc' : 'desc';
        this.setState({ sortOrder: newSortOrder });
        // Data will be sorted in renderTableRows based on sortOrder
    };

    applyFilter = () => {
        this.setState({ currentPage: 0 }, () => {
            this.fetchData();
        });
    };

    clearFilter = () => {
        // Calculate current week again
        const today = new Date();
        const dayOfWeek = today.getDay();
        const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const monday = new Date(today);
        monday.setDate(today.getDate() + diffToMonday);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        
        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        this.setState({
            filterType: "USER",
            dateFrom: formatDate(monday),
            dateTo: formatDate(sunday),
            currentPage: 0,
        }, () => {
            this.fetchData();
        });
    };

    renderPagination = () => {
        let { currentPage, totalPages } = this.state;
        
        // Always show pagination (even if only one page or no data)
        if (totalPages === 0) {
            totalPages = 1; // At least show one page
        }

        const pageNumbers = [];
        
        // Show page numbers with ellipsis if too many
        const maxVisiblePages = 10;
        let startPage = Math.max(0, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);
        
        if (endPage - startPage < maxVisiblePages) {
            startPage = Math.max(0, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage && i < totalPages; i++) {
            pageNumbers.push(i);
        }

        return (
            <nav aria-label="Reports KPI pagination">
                <Pagination className="pagination justify-content-center mt-4">
                    <PaginationItem disabled={currentPage === 0}>
                        <PaginationLink
                            href="#prev"
                            onClick={(e) => {
                                e.preventDefault();
                                if (currentPage > 0) this.handlePageChange(currentPage - 1);
                            }}
                        >
                            <i className="fa fa-angle-left" />
                        </PaginationLink>
                    </PaginationItem>
                    {startPage > 0 && (
                        <>
                            <PaginationItem>
                                <PaginationLink
                                    href="#page-0"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        this.handlePageChange(0);
                                    }}
                                >
                                    1
                                </PaginationLink>
                            </PaginationItem>
                            {startPage > 1 && (
                                <PaginationItem disabled>
                                    <PaginationLink>...</PaginationLink>
                                </PaginationItem>
                            )}
                        </>
                    )}
                    {pageNumbers.length === 0 && totalPages > 0 ? (
                        <PaginationItem active>
                            <PaginationLink>1</PaginationLink>
                        </PaginationItem>
                    ) : (
                        pageNumbers.map((num) => (
                            <PaginationItem key={num} active={num === currentPage}>
                                <PaginationLink
                                    href={`#page-${num}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        this.handlePageChange(num);
                                    }}
                                >
                                    {num + 1}
                                </PaginationLink>
                            </PaginationItem>
                        ))
                    )}
                    {endPage < totalPages - 1 && (
                        <>
                            {endPage < totalPages - 2 && (
                                <PaginationItem disabled>
                                    <PaginationLink>...</PaginationLink>
                                </PaginationItem>
                            )}
                            <PaginationItem>
                                <PaginationLink
                                    href={`#page-${totalPages - 1}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        this.handlePageChange(totalPages - 1);
                                    }}
                                >
                                    {totalPages}
                                </PaginationLink>
                            </PaginationItem>
                        </>
                    )}
                    <PaginationItem disabled={currentPage >= totalPages - 1}>
                        <PaginationLink
                            href="#next"
                            onClick={(e) => {
                                e.preventDefault();
                                if (currentPage < totalPages - 1) this.handlePageChange(currentPage + 1);
                            }}
                        >
                            <i className="fa fa-angle-right" />
                        </PaginationLink>
                    </PaginationItem>
                </Pagination>
            </nav>
        );
    };

    renderTableHeaders = () => {
        const { filterType, sortOrder } = this.state;
        const sortIcon = sortOrder === 'desc' ? 'fa fa-arrow-down' : 'fa fa-arrow-up';
        
        if (filterType === "USER") {
            return (
                <thead className="thead-light">
                    <tr>
                        <th scope="col">User Name</th>
                        <th scope="col">Email</th>
                        <th 
                            scope="col" 
                            style={{ cursor: 'pointer' }}
                            onClick={this.handleSort}
                        >
                            Total Reports Created
                            <i className={`${sortIcon} ml-2`} />
                        </th>
                        <th scope="col">Latest Report Date</th>
                    </tr>
                </thead>
            );
        } else if (filterType === "COUNTRY") {
            return (
                <thead className="thead-light">
                    <tr>
                        <th scope="col">Country</th>
                        <th 
                            scope="col" 
                            style={{ cursor: 'pointer' }}
                            onClick={this.handleSort}
                        >
                            Total Reports Created
                            <i className={`${sortIcon} ml-2`} />
                        </th>
                        <th scope="col">Latest Report Date</th>
                    </tr>
                </thead>
            );
        } else if (filterType === "COMPANY") {
            return (
                <thead className="thead-light">
                    <tr>
                        <th scope="col">Company Name</th>
                        <th scope="col">Country</th>
                        <th 
                            scope="col" 
                            style={{ cursor: 'pointer' }}
                            onClick={this.handleSort}
                        >
                            Total Reports Created
                            <i className={`${sortIcon} ml-2`} />
                        </th>
                        <th scope="col">Latest Report Date</th>
                    </tr>
                </thead>
            );
        }
    };

    renderTableRows = () => {
        const { data, filterType, sortOrder } = this.state;
        
        if (!data || data.length === 0) {
            const colSpan = filterType === "USER" ? 4 : filterType === "COUNTRY" ? 3 : 4;
            return (
                <tbody>
                    <tr>
                        <td colSpan={colSpan} className="text-center py-4">
                            <p className="text-muted">No data available</p>
                        </td>
                    </tr>
                </tbody>
            );
        }

        // Sort data by totalReportsCreated
        const sortedData = [...data].sort((a, b) => {
            const aValue = a.totalReportsCreated || 0;
            const bValue = b.totalReportsCreated || 0;
            if (sortOrder === 'desc') {
                return bValue - aValue; // Descending: higher values first
            } else {
                return aValue - bValue; // Ascending: lower values first
            }
        });

        if (filterType === "USER") {
            return (
                <tbody className="list">
                    {sortedData.map((item, index) => (
                        <tr key={item.userId || index}>
                            <td>{item.userName || 'N/A'}</td>
                            <td>{item.emailAddress || 'N/A'}</td>
                            <td>{item.totalReportsCreated || 0}</td>
                            <td>{item.latestReportDate || 'N/A'}</td>
                        </tr>
                    ))}
                </tbody>
            );
        } else if (filterType === "COUNTRY") {
            return (
                <tbody className="list">
                    {sortedData.map((item, index) => (
                        <tr key={item.country || index}>
                            <td>{item.country || 'N/A'}</td>
                            <td>{item.totalReportsCreated || 0}</td>
                            <td>{item.latestReportDate || 'N/A'}</td>
                        </tr>
                    ))}
                </tbody>
            );
        } else if (filterType === "COMPANY") {
            return (
                <tbody className="list">
                    {sortedData.map((item, index) => (
                        <tr key={item.companyUID || index}>
                            <td>{item.companyName || 'N/A'}</td>
                            <td>{item.country || 'N/A'}</td>
                            <td>{item.totalReportsCreated || 0}</td>
                            <td>{item.latestReportDate || 'N/A'}</td>
                        </tr>
                    ))}
                </tbody>
            );
        }
    };

    render() {
        // Check visibility - NOT visible to ROLE_FREE, ROLE_LEVEL1, ROLE_LEVEL2
        // ROLE_LEVEL3 can access but will only see their own data (handled by backend)
        const hiddenRoles = ["ROLE_FREE", "ROLE_LEVEL1", "ROLE_LEVEL2"];
        if (SecurityService.hasAnyRole(hiddenRoles)) {
            return null;
        }

        const { filterType, dateFrom, dateTo, pageSize, loading, data } = this.state;

        return (
            <Card className="mt-4">
                <CardHeader className="border-0">
                    <Row className="align-items-center">
                        <div className="col">
                            <h3 className="mb-0">Reports KPI</h3>
                        </div>
                    </Row>
                    <Row className="mt-3">
                        {!(SecurityService.hasRole("ROLE_LEVEL3") || SecurityService.hasRole("ROLE_LEVEL_3")) && (
                            <Col md="2">
                                <FormInput
                                    name="filterType"
                                    type="select"
                                    label="Aggregation Type"
                                    placeholder="Select type"
                                    value={filterType}
                                    onChange={this.onChange}
                                    formGroupClassName="mb-0"
                                >
                                    <option value="USER">Users</option>
                                    <option value="COUNTRY">Countries</option>
                                    <option value="COMPANY">Companies</option>
                                </FormInput>
                            </Col>
                        )}
                        <Col md="2">
                            <FormInput
                                name="dateFrom"
                                type="date"
                                label="Date From"
                                placeholder="Date From"
                                value={dateFrom || ""}
                                onChange={this.onChange}
                                formGroupClassName="mb-0"
                            />
                        </Col>
                        <Col md="2">
                            <FormInput
                                name="dateTo"
                                type="date"
                                label="Date To"
                                placeholder="Date To"
                                value={dateTo || ""}
                                onChange={this.onChange}
                                formGroupClassName="mb-0"
                            />
                        </Col>
                        <Col md="2">
                            <FormInput
                                name="pageSize"
                                type="select"
                                label="Records Per Page"
                                placeholder="Records per page"
                                value={pageSize}
                                onChange={this.onChange}
                                formGroupClassName="mb-0"
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value={500}>500</option>
                            </FormInput>
                        </Col>
                        <Col sm="3" style={{ marginTop: "30px" }}>
                            <Button
                                size="md"
                                outline
                                color="default"
                                type="button"
                                onClick={this.applyFilter}
                                disabled={loading}
                            >
                                Apply Filter
                            </Button>
                            <Button
                                color="default"
                                size="md"
                                outline
                                type="button"
                                onClick={this.clearFilter}
                                disabled={loading}
                            >
                                Clear Filter
                            </Button>
                        </Col>
                    </Row>
                </CardHeader>
                <CardBody>
                    {loading ? (
                        <div className="text-center py-4">
                            <p>Loading...</p>
                        </div>
                    ) : (
                        <>
                            <Table className="align-items-center table-flush" responsive>
                                {this.renderTableHeaders()}
                                {this.renderTableRows()}
                            </Table>
                            {this.renderPagination()}
                            {data && data.length === 0 && (
                                <div className="text-center py-4">
                                    <p className="text-muted">No data available for the selected filters</p>
                                </div>
                            )}
                        </>
                    )}
                </CardBody>
            </Card>
        );
    }
}

export default ReportsKpiTable;

